/**
 * KB Monitor Admin Page — Super Admin only
 * Manual trigger for ATO/ASIC knowledge base checks + status view.
 */

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, CheckCircle2, AlertTriangle, Clock, Shield, BookOpen, ExternalLink, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/shared/PageHeader';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import apiClient from '@/services/api/client';

export default function KBMonitorPage() {
  const queryClient = useQueryClient();
  const [running, setRunning] = useState<'ato' | 'asic' | null>(null);

  const { data: status, isLoading } = useQuery({
    queryKey: ['kb-monitor-status'],
    queryFn: async () => { const r = await apiClient.get('/admin/kb-monitor/status'); return r.data.data; },
    staleTime: 60 * 1000,
  });

  const runAtoAsicCheck = async () => {
    setRunning('ato');
    try {
      const r = await apiClient.post('/admin/kb-monitor/run');
      const d = r.data.data;
      toast.success(`ATO/ASIC check complete: ${d?.checked || 0} sources checked, ${d?.changedCount || 0} changed`);
      queryClient.invalidateQueries({ queryKey: ['kb-monitor-status'] });
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to run check');
    }
    setRunning(null);
  };

  const runAsicImport = async () => {
    setRunning('asic');
    try {
      const r = await apiClient.post('/admin/asic-kb-import');
      const d = r.data.data;
      toast.success(`ASIC import complete: ${d?.documentsImported || d?.imported || 0} documents imported`);
      queryClient.invalidateQueries({ queryKey: ['kb-monitor-status'] });
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to import');
    }
    setRunning(null);
  };

  const items = status?.items || [];
  const atoItems = items.filter((i: any) => i.source === 'ato');
  const asicItems = items.filter((i: any) => i.source === 'asic');

  return (
    <div className="space-y-6">
      <PageHeader
        icon={RefreshCw}
        title="KB Monitor — ATO & ASIC"
        description="Daily automated checks for regulatory changes. Manual triggers below."
      />

      {/* Action buttons */}
      <div className="flex gap-3 flex-wrap">
        <Button onClick={runAtoAsicCheck} disabled={running !== null} className="gap-2">
          {running === 'ato' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Run ATO + ASIC Check Now
        </Button>
        <Button onClick={runAsicImport} disabled={running !== null} variant="outline" className="gap-2">
          {running === 'asic' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
          Re-import ASIC KB
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold">{items.length}</p>
            <p className="text-[10px] text-muted-foreground uppercase">Total Sources Tracked</p>
          </CardContent>
        </Card>
        <Card className="border-blue-500/30">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{atoItems.length}</p>
            <p className="text-[10px] text-muted-foreground uppercase">ATO Sources</p>
          </CardContent>
        </Card>
        <Card className="border-indigo-500/30">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-indigo-600">{asicItems.length}</p>
            <p className="text-[10px] text-muted-foreground uppercase">ASIC Sources</p>
          </CardContent>
        </Card>
        <Card className="border-green-500/30">
          <CardContent className="pt-4 pb-4 text-center">
            <p className="text-2xl font-bold text-green-600">{items.filter((i: any) => i.imported_to_kb).length}</p>
            <p className="text-[10px] text-muted-foreground uppercase">Imported to KB</p>
          </CardContent>
        </Card>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* ATO Sources */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <BookOpen className="h-4 w-4" /> ATO Sources ({atoItems.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="px-3 py-2 text-left font-medium">Title</th>
                    <th className="px-3 py-2 text-left font-medium">Category</th>
                    <th className="px-3 py-2 text-left font-medium">Last Checked</th>
                    <th className="px-3 py-2 text-left font-medium">Last Changed</th>
                    <th className="px-3 py-2 text-center font-medium">In KB</th>
                    <th className="px-3 py-2 text-left font-medium">URL</th>
                  </tr>
                </thead>
                <tbody>
                  {atoItems.map((item: any, i: number) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="px-3 py-2 font-medium">{item.title}</td>
                      <td className="px-3 py-2"><Badge variant="outline" className="text-[9px]">{item.category}</Badge></td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {item.last_checked_at ? new Date(item.last_checked_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {item.last_changed_at ? new Date(item.last_changed_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {item.imported_to_kb ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500 mx-auto" /> : <Clock className="h-3.5 w-3.5 text-muted-foreground mx-auto" />}
                      </td>
                      <td className="px-3 py-2">
                        <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" /> View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* ASIC Sources */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Shield className="h-4 w-4" /> ASIC Sources ({asicItems.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="px-3 py-2 text-left font-medium">Title</th>
                    <th className="px-3 py-2 text-left font-medium">Category</th>
                    <th className="px-3 py-2 text-left font-medium">Last Checked</th>
                    <th className="px-3 py-2 text-left font-medium">Last Changed</th>
                    <th className="px-3 py-2 text-center font-medium">In KB</th>
                    <th className="px-3 py-2 text-left font-medium">URL</th>
                  </tr>
                </thead>
                <tbody>
                  {asicItems.map((item: any, i: number) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="px-3 py-2 font-medium">{item.title}</td>
                      <td className="px-3 py-2"><Badge variant="outline" className="text-[9px]">{item.category}</Badge></td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {item.last_checked_at ? new Date(item.last_checked_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {item.last_changed_at ? new Date(item.last_changed_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {item.imported_to_kb ? <CheckCircle2 className="h-3.5 w-3.5 text-green-500 mx-auto" /> : <Clock className="h-3.5 w-3.5 text-muted-foreground mx-auto" />}
                      </td>
                      <td className="px-3 py-2">
                        <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline flex items-center gap-1">
                          <ExternalLink className="h-3 w-3" /> View
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* Info */}
          <div className="p-3 rounded-md bg-blue-500/5 border border-blue-500/20 text-[11px] text-muted-foreground leading-relaxed">
            <strong className="text-foreground">Automated schedule:</strong> The KB Monitor runs daily at 6:00 AM AEST.
            It checks all {items.length} sources, compares content hashes, and auto-imports any changes to the Knowledge Base.
            You receive an email notification when content changes are detected.
          </div>
        </>
      )}
    </div>
  );
}
